import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Video, ChevronLeft, ChevronRight, Plus, Trash2, ExternalLink, GripVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";

/** Shoots Calendar — monthly grid mixing LIM Flow shoots (red dot) with the
 *  LIME Studios external bookings feed (blue dot). Drag-drop a shoot card from
 *  the day panel onto another date to reschedule. Right-hand panel shows both
 *  sources for the selected day. */
const toIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function monthDays(year, month) {
  // Return an array of Date objects for a 6-week grid starting on the Monday
  // of the week containing day 1. Mon = 0 offset.
  const first = new Date(year, month, 1);
  const startOffset = (first.getDay() + 6) % 7;
  const start = new Date(first);
  start.setDate(1 - startOffset);
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start); d.setDate(start.getDate() + i); return d;
  });
}

export default function ShootsCalendar() {
  const { user } = useAuth();
  const nav = useNavigate();
  const canView = !!user && (user.is_super_admin || user.is_admin || user.team === "Shoot Team");
  const now = new Date();
  const [cursor, setCursor] = useState({ year: now.getFullYear(), month: now.getMonth() });
  const [shoots, setShoots] = useState([]);
  const [external, setExternal] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selDay, setSelDay] = useState(toIso(now));
  const [dragId, setDragId] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [expandedExt, setExpandedExt] = useState({});

  const load = async () => {
    setLoading(true);
    const first = new Date(cursor.year, cursor.month, 1);
    const last = new Date(cursor.year, cursor.month + 1, 0);
    try {
      const [s, e] = await Promise.all([
        api.get("/shoots", { params: { date_from: toIso(first), date_to: toIso(last) } }),
        api.get("/external-bookings").catch(() => ({ data: [] })),
      ]);
      setShoots(s.data || []);
      setExternal((e.data || []).filter((b) => (b.date || "") >= toIso(first) && (b.date || "") <= toIso(last)));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { if (canView) load(); }, [canView, cursor.year, cursor.month]);

  const shootsByDay = useMemo(() => {
    const map = new Map();
    shoots.forEach((s) => {
      if (!map.has(s.shoot_date)) map.set(s.shoot_date, []);
      map.get(s.shoot_date).push(s);
    });
    return map;
  }, [shoots]);
  const externalByDay = useMemo(() => {
    const map = new Map();
    external.forEach((b) => {
      if (!map.has(b.date)) map.set(b.date, []);
      map.get(b.date).push(b);
    });
    return map;
  }, [external]);

  const days = useMemo(() => monthDays(cursor.year, cursor.month), [cursor]);

  const shiftMonth = (d) => setCursor((c) => {
    let m = c.month + d, y = c.year;
    if (m < 0) { m = 11; y -= 1; }
    if (m > 11) { m = 0; y += 1; }
    return { year: y, month: m };
  });

  const onDrop = async (targetIso) => {
    if (!dragId) return;
    const s = shoots.find((x) => x.id === dragId);
    setDragId(null); setDragOver(null);
    if (!s || s.shoot_date === targetIso) return;
    const prev = shoots;
    setShoots(shoots.map((x) => (x.id === dragId ? { ...x, shoot_date: targetIso } : x)));
    try {
      await api.patch(`/shoots/${dragId}`, { shoot_date: targetIso });
      toast.success(`Moved to ${targetIso}`);
    } catch (err) {
      setShoots(prev);
      toast.error("Move failed — reverted");
    }
  };

  const deleteShoot = async (s) => {
    if (!window.confirm(`Delete shoot for ${s.client} on ${s.shoot_date}?`)) return;
    try {
      await api.delete(`/shoots/${s.id}`);
      setShoots((list) => list.filter((x) => x.id !== s.id));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to delete");
    }
  };

  // Week-by-week summary for the visible month
  const weekly = useMemo(() => {
    const firstMonth = new Date(cursor.year, cursor.month, 1);
    const lastMonth = new Date(cursor.year, cursor.month + 1, 0);
    const weeks = [];
    let cur = new Date(firstMonth);
    cur.setDate(cur.getDate() - ((cur.getDay() + 6) % 7)); // back to Monday
    while (cur <= lastMonth) {
      const end = new Date(cur); end.setDate(end.getDate() + 6);
      let count = 0;
      shoots.forEach((s) => {
        const d = new Date(s.shoot_date);
        if (d >= cur && d <= end) count += 1;
      });
      weeks.push({ from: toIso(new Date(cur)), to: toIso(end), count });
      cur = new Date(cur); cur.setDate(cur.getDate() + 7);
    }
    const max = Math.max(1, ...weeks.map((w) => w.count));
    const totalMonth = shoots.filter((s) => {
      const d = new Date(s.shoot_date);
      return d >= firstMonth && d <= lastMonth;
    }).length;
    return { weeks, max, totalMonth };
  }, [shoots, cursor]);

  if (!canView) return <div className="p-6 text-white/60">Shoot Team access only.</div>;

  const monthName = new Date(cursor.year, cursor.month, 1).toLocaleString("en-IN", { month: "long", year: "numeric" });

  return (
    <div className="p-4 sm:p-6 space-y-5" data-testid="shoots-calendar-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[0.7rem] uppercase tracking-[0.3em] text-white/40 flex items-center gap-2"><Video size={12} /> Shoots</div>
          <h1 className="text-3xl sm:text-4xl font-display tracking-tight">{monthName}</h1>
          <p className="text-sm text-white/50 mt-1">
            <span className="inline-flex items-center gap-1 mr-3"><span className="h-1.5 w-1.5 rounded-full bg-red-500" /> LIM Flow ({weekly.totalMonth})</span>
            <span className="inline-flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-sky-400" /> LIME Studios ({external.length})</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={() => shiftMonth(-1)} data-testid="shoots-prev-month" className="h-9 w-9 p-0"><ChevronLeft size={16} /></Button>
          <Button variant="ghost" onClick={() => shiftMonth(1)} data-testid="shoots-next-month" className="h-9 w-9 p-0"><ChevronRight size={16} /></Button>
          <Button onClick={() => nav(`/shoots/new?date=${selDay}`)} className="bg-[#E11D2E] hover:bg-[#F03041] rounded-full px-5" data-testid="shoots-new-btn">
            <Plus size={13} className="mr-1.5" /> New shoot
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-5">
        {/* Grid */}
        <div className="rounded-lg border border-white/10 bg-black/25 p-3">
          <div className="grid grid-cols-7 gap-1 text-[0.7rem] uppercase tracking-widest text-white/40 pb-2">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d} className="text-center">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {days.map((d) => {
              const iso = toIso(d);
              const isCurrentMonth = d.getMonth() === cursor.month;
              const entries = [
                ...(shootsByDay.get(iso) || []).map((s) => ({ id: s.id, kind: "lim", time: s.call_time || "", name: s.client })),
                ...(externalByDay.get(iso) || []).map((b) => ({ id: b.id, kind: "ext", time: b.start_time || "", name: b.customer_name || b.title || "Booking" })),
              ].sort((a, b) => (a.time || "99").localeCompare(b.time || "99"));
              const rest = entries.length - 3;
              const isSel = selDay === iso;
              const isDragTarget = dragOver === iso;
              return (
                <button
                  key={iso}
                  onClick={() => setSelDay(iso)}
                  onDragOver={(e) => { if (dragId) { e.preventDefault(); setDragOver(iso); } }}
                  onDragLeave={() => setDragOver((cur) => (cur === iso ? null : cur))}
                  onDrop={(e) => { e.preventDefault(); onDrop(iso); }}
                  data-testid={`shoots-day-${iso}`}
                  className={`text-left min-h-[6rem] rounded-md flex flex-col border p-1.5 transition ${isCurrentMonth ? "bg-black/30" : "bg-black/10 text-white/25"} ${isSel ? "border-[#E11D2E]/60" : "border-white/10"} ${isDragTarget ? "ring-2 ring-emerald-400/60" : ""}`}
                >
                  <span className="text-xs font-mono">{d.getDate()}</span>
                  <div className="mt-1 space-y-0.5 min-w-0 w-full">
                    {entries.slice(0, 3).map((e) => (
                      <div key={`${e.kind}-${e.id}`} title={`${e.time ? `${e.time} · ` : ""}${e.name}`} data-testid={`shoots-cell-entry-${e.kind}-${e.id}`}
                        className={`line-clamp-2 break-words rounded px-1 py-0.5 text-[0.68rem] leading-tight border-l-2 ${e.kind === "lim" ? "border-red-500 bg-red-500/10 text-red-700 dark:text-red-200" : "border-sky-400 bg-sky-500/10 text-sky-700 dark:text-sky-200"}`}>
                        {e.time && <span className="font-mono opacity-80 mr-1">{e.time}</span>}<span className="font-medium">{e.name}</span>
                      </div>
                    ))}
                    {rest > 0 && <div className="text-[0.65rem] text-white/50 font-mono px-1" data-testid={`shoots-cell-more-${iso}`}>+{rest} more</div>}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Day panel */}
        <div className="rounded-lg border border-white/10 bg-black/25 p-4 h-fit xl:sticky xl:top-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="overline flex items-center gap-2"><Video size={11} /> {selDay}</div>
              <div className="text-sm text-white/50">{(shootsByDay.get(selDay) || []).length} shoot(s) · {(externalByDay.get(selDay) || []).length} booking(s)</div>
            </div>
            <Button size="sm" onClick={() => nav(`/shoots/new?date=${selDay}`)} className="bg-[#E11D2E] hover:bg-[#F03041] h-8" data-testid="shoots-day-add-btn">
              <Plus size={12} className="mr-1" /> Add
            </Button>
          </div>

          {/* LIM Flow shoots (draggable) */}
          <div className="space-y-2 mb-4">
            {(shootsByDay.get(selDay) || []).map((s) => (
              <div
                key={s.id}
                draggable
                onDragStart={() => setDragId(s.id)}
                onDragEnd={() => { setDragId(null); setDragOver(null); }}
                data-testid={`shoots-card-${s.id}`}
                className="rounded-md border border-red-500/30 bg-red-500/[0.05] p-2.5 cursor-grab active:cursor-grabbing hover:border-red-500/60 transition"
              >
                <div className="flex items-start gap-2">
                  <GripVertical size={12} className="text-white/40 mt-1 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{s.client}</div>
                    <div className="text-[0.7rem] text-white/50 font-mono">{s.call_time || "—"} · {s.location_type} · {(s.items || []).length} items</div>
                  </div>
                  <Link to={`/shoots/${s.id}/edit`} className="h-6 w-6 rounded flex items-center justify-center hover:bg-white/10 text-white/60" title="Open"><ExternalLink size={12} /></Link>
                  <button onClick={() => deleteShoot(s)} className="h-6 w-6 rounded flex items-center justify-center hover:bg-red-500/20 text-red-300" title="Delete" data-testid={`shoots-delete-${s.id}`}><Trash2 size={12} /></button>
                </div>
              </div>
            ))}
            {(shootsByDay.get(selDay) || []).length === 0 && <div className="text-white/40 text-xs italic">No LIM Flow shoots on this day.</div>}
          </div>

          {/* LIME Studios feed */}
          <div className="border-t border-white/5 pt-3">
            <div className="overline mb-2 text-sky-300">LIME Studios feed</div>
            <div className="space-y-1.5">
              {(externalByDay.get(selDay) || []).length === 0 && <div className="text-white/40 text-xs italic">Nothing from LIME today.</div>}
              {(externalByDay.get(selDay) || []).map((b) => (
                <div key={b.id} className="rounded-md border border-sky-500/30 bg-sky-500/[0.05] p-2 text-xs">
                  <button onClick={() => setExpandedExt((s) => ({ ...s, [b.id]: !s[b.id] }))} className="w-full text-left flex items-center justify-between gap-2" data-testid={`shoots-ext-${b.id}`}>
                    <span className="truncate"><b className="text-white/80">{b.customer_name || b.title}</b>{b.start_time ? ` · ${b.start_time}` : ""}</span>
                    <span className="text-[0.65rem] text-white/40 font-mono">{b.status || ""}</span>
                  </button>
                  {expandedExt[b.id] && (
                    <pre className="mt-2 text-[0.7rem] text-white/60 font-mono whitespace-pre-wrap">{b.notes || ""}</pre>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Weekly summary */}
      <div className="rounded-lg border border-white/10 bg-black/25 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="overline">Week-by-week</div>
          <div className="text-[0.7rem] text-white/40 font-mono">Total {weekly.totalMonth} shoots this month</div>
        </div>
        <div className="space-y-2">
          {weekly.weeks.map((w) => {
            const pct = (w.count / weekly.max) * 100;
            const isPeak = w.count === weekly.max && w.count > 0;
            return (
              <div key={w.from} className="flex items-center gap-3 text-xs">
                <div className="w-32 font-mono text-white/60">{w.from} → {w.to}</div>
                <div className="flex-1 h-2 bg-white/5 rounded overflow-hidden">
                  <div className="h-full bg-[#E11D2E]" style={{ width: `${pct}%` }} />
                </div>
                <div className="w-10 text-right font-mono">{w.count}</div>
                {isPeak && <span className="text-[0.65rem] uppercase tracking-widest text-red-300 bg-red-500/10 border border-red-500/40 rounded-full px-1.5 py-0.5">Peak</span>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
